/**
 * Session Context
 *
 * Manages the ECS Fargate session backend lifecycle in cloud mode.
 * - On login: requests a session, polls until RUNNING, starts heartbeat
 * - On logout / unmount: ends session, clears heartbeat
 * - Provides session state to the rest of the app
 *
 * In local mode this context is essentially a no-op; the session is always
 * considered "ready" because the backend is running locally.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { sessionApi } from '../services/cloudApi';
import { useAuth } from './AuthContext';

const DEPLOYMENT_MODE = process.env.REACT_APP_DEPLOYMENT_MODE || 'local';
console.log(`[Session] DEPLOYMENT_MODE=${DEPLOYMENT_MODE} (raw env: ${process.env.REACT_APP_DEPLOYMENT_MODE ?? 'undefined'})`);
const HEARTBEAT_INTERVAL_MS = 30_000;
const POLL_INTERVAL_MS = 2_000;
const MAX_POLL_DURATION_MS = 240_000; // 4 min — above backend's 120s ECS timeout + 90s health probe
const MAX_HEARTBEAT_FAILURES = 5;

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
    const { isAuthenticated, registerPreLogoutCallback } = useAuth();

    const [sessionId, setSessionId] = useState(null);
    const [sessionStatus, setSessionStatus] = useState(null); // PROVISIONING | STARTING | RUNNING | FAILED | null
    const [isReady, setIsReady] = useState(DEPLOYMENT_MODE === 'local');
    const [error, setError] = useState(null);

    const heartbeatRef = useRef(null);
    const pollRef = useRef(null);
    const heartbeatFailCountRef = useRef(0);
    // Ref mirrors sessionId so the pre-logout callback never has a stale closure
    const sessionIdRef = useRef(null);

    const clearTimers = useCallback(() => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const startHeartbeat = useCallback(() => {
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        heartbeatFailCountRef.current = 0;

        heartbeatRef.current = setInterval(async () => {
            try {
                const result = await sessionApi.heartbeat();
                heartbeatFailCountRef.current = 0;

                // If heartbeat returns 404, session was reaped server-side
                if (!result) {
                    console.warn('[Session] Heartbeat: session gone (null response)');
                    clearTimers();
                    setIsReady(false);
                    setSessionStatus('FAILED');
                    setError('Your session expired. Please refresh to start a new one.');
                }
            } catch (err) {
                heartbeatFailCountRef.current += 1;
                const fails = heartbeatFailCountRef.current;
                console.warn(`[Session] Heartbeat failed (${fails}/${MAX_HEARTBEAT_FAILURES}):`, err.message);

                if (err.message && err.message.includes('No active session found')) {
                    clearTimers();
                    setIsReady(false);
                    setSessionStatus('FAILED');
                    setError('Your session expired. Please refresh to start a new one.');
                    return;
                }

                if (fails >= MAX_HEARTBEAT_FAILURES) {
                    console.error('[Session] Too many heartbeat failures, marking session lost');
                    clearTimers();
                    setIsReady(false);
                    setSessionStatus('FAILED');
                    setError('Connection to your session was lost. Please try again.');
                }
            }
        }, HEARTBEAT_INTERVAL_MS);
    }, [clearTimers]);

    const pollUntilReady = useCallback((sid) => {
        const pollStart = Date.now();

        pollRef.current = setInterval(async () => {
            // Timeout guard
            if (Date.now() - pollStart > MAX_POLL_DURATION_MS) {
                clearInterval(pollRef.current);
                pollRef.current = null;
                setSessionStatus('FAILED');
                setError('Session is taking too long to start. Please try again.');
                return;
            }

            try {
                const session = await sessionApi.get();
                if (!session) return;
                setSessionStatus(session.status);

                if (session.status === 'RUNNING') {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    setIsReady(true);
                    startHeartbeat();
                } else if (session.status === 'FAILED' || session.status === 'STOPPED') {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    setError('Session backend failed to start. Please try again.');
                }
            } catch (err) {
                console.error('[Session] Poll error:', err);
            }
        }, POLL_INTERVAL_MS);
    }, [startHeartbeat]);

    const requestSession = useCallback(async ({ forceNew = false } = {}) => {
        if (DEPLOYMENT_MODE !== 'cloud') return;

        setError(null);
        setIsReady(false);
        setSessionStatus('PROVISIONING');

        try {
            const result = await sessionApi.request({ forceNew });
            setSessionId(result.session_id);
            sessionIdRef.current = result.session_id;
            setSessionStatus(result.status);

            if (result.status === 'RUNNING') {
                setIsReady(true);
                startHeartbeat();
            } else {
                pollUntilReady(result.session_id);
            }
        } catch (err) {
            console.error('[Session] Request failed:', err);
            setError(err.message || 'Failed to start session backend');
            setSessionStatus('FAILED');
        }
    }, [startHeartbeat, pollUntilReady]);

    const endSession = useCallback(async () => {
        clearTimers();
        const sid = sessionIdRef.current;
        if (sid && DEPLOYMENT_MODE === 'cloud') {
            try {
                await sessionApi.end(sid);
            } catch (err) {
                console.warn('[Session] End session error:', err.message);
            }
        }
        setSessionId(null);
        sessionIdRef.current = null;
        setSessionStatus(null);
        setIsReady(DEPLOYMENT_MODE === 'local');
        setError(null);
    }, [clearTimers]);

    /**
     * Retry session from scratch — stops any existing session and requests a
     * brand-new ECS task (force_new=true).  Used by the SessionBootScreen
     * retry button and useful for QA debugging.
     */
    const retrySession = useCallback(async () => {
        clearTimers();
        setSessionId(null);
        sessionIdRef.current = null;
        setSessionStatus(null);
        setIsReady(false);
        setError(null);
        await requestSession({ forceNew: true });
    }, [clearTimers, requestSession]);

    // End session on logout BEFORE the auth token is cleared.
    // The pre-logout hook runs while credentials are still valid so the
    // DELETE /api/sessions/{id} call can authenticate.
    useEffect(() => {
        if (DEPLOYMENT_MODE !== 'cloud') return;
        return registerPreLogoutCallback(() => endSession());
    }, [registerPreLogoutCallback, endSession]);

    // Request session when user authenticates in cloud mode
    useEffect(() => {
        if (DEPLOYMENT_MODE !== 'cloud') return;

        if (isAuthenticated) {
            requestSession();
        }
        // Logout cleanup is handled by the pre-logout callback above —
        // no need for an else branch here (the token would already be
        // cleared by the time this effect runs on isAuthenticated=false).

        return () => clearTimers();
    }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    const value = {
        sessionId,
        sessionStatus,
        isReady,
        error,
        isCloudMode: DEPLOYMENT_MODE === 'cloud',
        requestSession,
        endSession,
        retrySession,
    };

    return (
        <SessionContext.Provider value={value}>
            {children}
        </SessionContext.Provider>
    );
}

export function useSession() {
    const context = useContext(SessionContext);
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
}

export default SessionContext;
